/**
 * STP Parser WASM Bridge
 *
 * STEP dosyasından BREP topoloji bilgilerini çıkarır:
 * - ADVANCED_FACE → Surface (PLANE, CYLINDRICAL_SURFACE, vb.)
 * - EDGE_CURVE → Curve (LINE, CIRCLE, vb.)
 * - Birim dönüşümü (mm, m, inch)
 * - Radius grupları
 * - Center, axis, normal bilgileri
 */

import { FikoWasmBridge } from '../IFikoThreeJS/fiko-performance/FikoWasmBridge';

// Debug modu - production'da false olmalı
const DEBUG_STP_PARSER = false;

// ============================================================================
// TYPES
// ============================================================================

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

interface SurfaceInfo {
  surfaceType: string;
  radius?: number;
  majorRadius?: number;
  minorRadius?: number;
  semiAngle?: number;
  center?: Vec3;
  axis?: Vec3;
  refDirection?: Vec3;
}

interface CurveInfo {
  curveType: string;
  radius?: number;
  center?: Vec3;
  axis?: Vec3;
  startPoint?: Vec3;
  endPoint?: Vec3;
}

interface AdvancedFaceInfo {
  faceIndex: number;
  surfaceId: string;
  surfaceType: string;
  surfaceData: SurfaceInfo;
}

interface EdgeCurveInfo {
  edgeIndex: number;
  curveId: string;
  curveType: string;
  curveData: CurveInfo;
}

export interface StepTopologyResult {
  faces: Record<string, SurfaceInfo>;
  edges: Record<string, CurveInfo>;
  radiusGroups: Record<string, string[]>;
  advancedFaces: AdvancedFaceInfo[];
  edgeCurves: EdgeCurveInfo[];
  /** Radius bazlı face grupları */
  faceRadiusGroups: Record<string, number[]>;
  /** Radius bazlı edge grupları */
  edgeRadiusGroups: Record<string, number[]>;
  /** Birim çarpanı (1.0 = mm) */
  unitScale: number;
}

// ============================================================================
// WASM BRIDGE
// ============================================================================

async function getWasmModule(): Promise<any> {
  try {
    const bridge = FikoWasmBridge.getInstance();
    const module = await bridge.getWasmModule();
    return module || null;
  } catch (e) {
    return null;
  }
}

export async function parseStepTopologyWasm(stepContent: string): Promise<StepTopologyResult> {
  const wasmModule = await getWasmModule();

  if (wasmModule && wasmModule.parseStepTopology) {
    try {
      const start = performance.now();
      const result = wasmModule.parseStepTopology(stepContent);
      const elapsed = performance.now() - start;
      return JSON.parse(result) as StepTopologyResult;
    } catch (e) {
    }
  }

  return parseStepTopologyJs(stepContent);
}

// ============================================================================
// STEP PARSER - JAVASCRIPT
// ============================================================================

function parseStepTopologyJs(stepContent: string): StepTopologyResult {
  const startTime = performance.now();

  // Entity map build et
  const entityMap = buildEntityMap(stepContent);

  // Birim skalasını bul
  const unitScale = parseUnitScale(entityMap);

  // Tüm geometrik entity'leri parse et
  const cartesianPoints = parseCartesianPoints(entityMap);
  const directions = parseDirections(entityMap);
  const placements = parsePlacements(entityMap, cartesianPoints, directions);
  const surfaces = parseSurfaces(entityMap, placements, unitScale);
  const curves = parseCurves(entityMap, placements, cartesianPoints, unitScale);

  // ADVANCED_FACE → Surface mapping
  const advancedFaces = parseAdvancedFaces(entityMap, surfaces);

  // EDGE_CURVE → Curve mapping
  const edgeCurves = parseEdgeCurves(entityMap, curves);

  // Radius gruplarını oluştur
  const { faceRadiusGroups, edgeRadiusGroups, radiusGroups } = buildRadiusGroups(advancedFaces, edgeCurves);

  // faces ve edges record'larını oluştur
  const faces: Record<string, SurfaceInfo> = {};
  const edges: Record<string, CurveInfo> = {};

  for (const [id, surface] of Object.entries(surfaces)) {
    faces[id] = surface;
  }

  for (const [id, curve] of Object.entries(curves)) {
    edges[id] = curve;
  }

  const elapsed = performance.now() - startTime;

  return {
    faces,
    edges,
    radiusGroups,
    advancedFaces,
    edgeCurves,
    faceRadiusGroups,
    edgeRadiusGroups,
    unitScale
  };
}

// ============================================================================
// ENTITY MAP BUILDER
// ============================================================================

function buildEntityMap(content: string): Map<string, { type: string; params: string }> {
  const map = new Map<string, { type: string; params: string }>();

  // Satır satır işle (daha güvenilir)
  const lines = content.split(/[\r\n]+/);
  let currentEntity = '';

  for (const line of lines) {
    const trimmed = line.trim();

    // Yorum satırlarını atla
    if (trimmed.startsWith('/*') || trimmed.startsWith('*')) continue;

    currentEntity += trimmed;

    // Entity tamamlandı mı?
    if (currentEntity.endsWith(';')) {
      const match = currentEntity.match(/^#(\d+)\s*=\s*([A-Z_][A-Z0-9_]*)\s*\((.*)\)\s*;$/s);
      if (match) {
        const [, id, type, params] = match;
        map.set(id, { type, params });
      }
      currentEntity = '';
    }
  }

  return map;
}

// ============================================================================
// UNIT SCALE PARSER
// ============================================================================

function parseUnitScale(entityMap: Map<string, { type: string; params: string }>): number {
  // Varsayılan: mm (1.0)
  let scale = 1.0;

  for (const [id, { type, params }] of entityMap) {
    // SI_UNIT kontrolü
    if (type === 'SI_UNIT') {
      // SI_UNIT(*, .MILLI., .METRE.)
      if (params.includes('.MILLI.') && params.includes('.METRE.')) {
        scale = 1.0; // mm
      } else if (params.includes('.METRE.') && !params.includes('.MILLI.') && !params.includes('.CENTI.')) {
        scale = 1000.0; // m → mm
      } else if (params.includes('.CENTI.') && params.includes('.METRE.')) {
        scale = 10.0; // cm → mm
      }
    }

    // LENGTH_MEASURE veya CONVERSION_BASED_UNIT kontrolü
    if (type === 'CONVERSION_BASED_UNIT') {
      // Inch kontrolü
      if (params.toLowerCase().includes('inch')) {
        scale = 25.4; // inch → mm
      }
    }

    // GLOBAL_UNIT_ASSIGNED_CONTEXT kontrolü
    if (type === 'LENGTH_MEASURE') {
      const match = params.match(/(-?[\d.eE+-]+)/);
      if (match) {
        const value = parseFloat(match[1]);
        // Eğer değer çok küçükse (< 0.001), muhtemelen m cinsinden
        if (value > 0 && value < 0.001) {
          scale = 1000.0;
        }
      }
    }
  }

  return scale;
}

// ============================================================================
// CARTESIAN POINT PARSER
// ============================================================================

function parseCartesianPoints(entityMap: Map<string, { type: string; params: string }>): Map<string, Vec3> {
  const points = new Map<string, Vec3>();

  for (const [id, { type, params }] of entityMap) {
    if (type === 'CARTESIAN_POINT') {
      // CARTESIAN_POINT('', (x, y, z))
      const coordMatch = params.match(/\(\s*(-?[\d.eE+-]+)\s*,\s*(-?[\d.eE+-]+)\s*,\s*(-?[\d.eE+-]+)\s*\)/);
      if (coordMatch) {
        points.set(id, {
          x: parseFloat(coordMatch[1]),
          y: parseFloat(coordMatch[2]),
          z: parseFloat(coordMatch[3])
        });
      }
    }
  }

  return points;
}

// ============================================================================
// DIRECTION PARSER
// ============================================================================

function parseDirections(entityMap: Map<string, { type: string; params: string }>): Map<string, Vec3> {
  const directions = new Map<string, Vec3>();

  for (const [id, { type, params }] of entityMap) {
    if (type === 'DIRECTION') {
      // DIRECTION('', (x, y, z))
      const dirMatch = params.match(/\(\s*(-?[\d.eE+-]+)\s*,\s*(-?[\d.eE+-]+)\s*,\s*(-?[\d.eE+-]+)\s*\)/);
      if (dirMatch) {
        directions.set(id, {
          x: parseFloat(dirMatch[1]),
          y: parseFloat(dirMatch[2]),
          z: parseFloat(dirMatch[3])
        });
      }
    }
  }

  return directions;
}

// ============================================================================
// PLACEMENT PARSER
// ============================================================================

interface Placement {
  location: Vec3;
  axis: Vec3;
  refDirection: Vec3;
}

function parsePlacements(
  entityMap: Map<string, { type: string; params: string }>,
  points: Map<string, Vec3>,
  directions: Map<string, Vec3>
): Map<string, Placement> {
  const placements = new Map<string, Placement>();

  for (const [id, { type, params }] of entityMap) {
    if (type === 'AXIS2_PLACEMENT_3D') {
      // AXIS2_PLACEMENT_3D('', #point, #axis, #ref_dir)
      const refs = extractRefs(params);

      let location: Vec3 = { x: 0, y: 0, z: 0 };
      let axis: Vec3 = { x: 0, y: 0, z: 1 };
      let refDirection: Vec3 = { x: 1, y: 0, z: 0 };

      if (refs[0] && points.has(refs[0])) {
        location = points.get(refs[0])!;
      }
      if (refs[1] && directions.has(refs[1])) {
        axis = directions.get(refs[1])!;
      }
      if (refs[2] && directions.has(refs[2])) {
        refDirection = directions.get(refs[2])!;
      }

      placements.set(id, { location, axis, refDirection });
    }
  }

  return placements;
}

// ============================================================================
// SURFACE PARSER
// ============================================================================

function parseSurfaces(
  entityMap: Map<string, { type: string; params: string }>,
  placements: Map<string, Placement>,
  unitScale: number
): Record<string, SurfaceInfo> {
  const surfaces: Record<string, SurfaceInfo> = {};

  for (const [id, { type, params }] of entityMap) {
    let surfaceInfo: SurfaceInfo | null = null;

    switch (type) {
      case 'PLANE': {
        // PLANE('', #placement)
        const refs = extractRefs(params);
        const placement = refs[0] ? placements.get(refs[0]) : null;

        surfaceInfo = {
          surfaceType: 'PLANE',
          center: placement?.location,
          axis: placement?.axis,
          refDirection: placement?.refDirection
        };
        break;
      }

      case 'CYLINDRICAL_SURFACE': {
        // CYLINDRICAL_SURFACE('', #placement, radius)
        const refs = extractRefs(params);
        const numbers = extractNumbers(params);
        const placement = refs[0] ? placements.get(refs[0]) : null;

        surfaceInfo = {
          surfaceType: 'CYLINDRICAL_SURFACE',
          radius: numbers[0] ? numbers[0] * unitScale : 0,
          center: placement?.location,
          axis: placement?.axis,
          refDirection: placement?.refDirection
        };
        break;
      }

      case 'CONICAL_SURFACE': {
        // CONICAL_SURFACE('', #placement, radius, semi_angle)
        const refs = extractRefs(params);
        const numbers = extractNumbers(params);
        const placement = refs[0] ? placements.get(refs[0]) : null;

        surfaceInfo = {
          surfaceType: 'CONICAL_SURFACE',
          radius: numbers[0] ? numbers[0] * unitScale : 0,
          semiAngle: numbers[1] || 0, // Radyan cinsinden
          center: placement?.location,
          axis: placement?.axis,
          refDirection: placement?.refDirection
        };
        break;
      }

      case 'SPHERICAL_SURFACE': {
        // SPHERICAL_SURFACE('', #placement, radius)
        const refs = extractRefs(params);
        const numbers = extractNumbers(params);
        const placement = refs[0] ? placements.get(refs[0]) : null;

        surfaceInfo = {
          surfaceType: 'SPHERICAL_SURFACE',
          radius: numbers[0] ? numbers[0] * unitScale : 0,
          center: placement?.location,
          axis: placement?.axis
        };
        break;
      }

      case 'TOROIDAL_SURFACE': {
        // TOROIDAL_SURFACE('', #placement, major_radius, minor_radius)
        const refs = extractRefs(params);
        const numbers = extractNumbers(params);
        const placement = refs[0] ? placements.get(refs[0]) : null;

        surfaceInfo = {
          surfaceType: 'TOROIDAL_SURFACE',
          majorRadius: numbers[0] ? numbers[0] * unitScale : 0,
          minorRadius: numbers[1] ? numbers[1] * unitScale : 0,
          radius: numbers[1] ? numbers[1] * unitScale : 0, // Minor radius'u ana radius olarak kullan
          center: placement?.location,
          axis: placement?.axis
        };
        break;
      }

      case 'B_SPLINE_SURFACE_WITH_KNOTS':
      case 'B_SPLINE_SURFACE': {
        surfaceInfo = {
          surfaceType: 'B_SPLINE_SURFACE'
        };
        break;
      }

      case 'SURFACE_OF_REVOLUTION': {
        // SURFACE_OF_REVOLUTION('', #curve, #placement)
        const refs = extractRefs(params);
        const placement = refs[1] ? placements.get(refs[1]) : null;

        surfaceInfo = {
          surfaceType: 'SURFACE_OF_REVOLUTION',
          center: placement?.location,
          axis: placement?.axis
        };
        break;
      }

      case 'SURFACE_OF_LINEAR_EXTRUSION': {
        surfaceInfo = {
          surfaceType: 'SURFACE_OF_LINEAR_EXTRUSION'
        };
        break;
      }
    }

    if (surfaceInfo) {
      surfaces[id] = surfaceInfo;
    }
  }

  return surfaces;
}

// ============================================================================
// CURVE PARSER
// ============================================================================

function parseCurves(
  entityMap: Map<string, { type: string; params: string }>,
  placements: Map<string, Placement>,
  points: Map<string, Vec3>,
  unitScale: number
): Record<string, CurveInfo> {
  const curves: Record<string, CurveInfo> = {};

  for (const [id, { type, params }] of entityMap) {
    let curveInfo: CurveInfo | null = null;

    switch (type) {
      case 'LINE': {
        // LINE('', #point, #vector)
        const refs = extractRefs(params);
        const startPoint = refs[0] ? points.get(refs[0]) : undefined;

        curveInfo = {
          curveType: 'LINE',
          startPoint
        };
        break;
      }

      case 'CIRCLE': {
        // CIRCLE('', #placement, radius)
        const refs = extractRefs(params);
        const numbers = extractNumbers(params);
        const placement = refs[0] ? placements.get(refs[0]) : null;

        curveInfo = {
          curveType: 'CIRCLE',
          radius: numbers[0] ? numbers[0] * unitScale : 0,
          center: placement?.location,
          axis: placement?.axis
        };
        break;
      }

      case 'ELLIPSE': {
        // ELLIPSE('', #placement, semi_axis1, semi_axis2)
        const refs = extractRefs(params);
        const numbers = extractNumbers(params);
        const placement = refs[0] ? placements.get(refs[0]) : null;

        curveInfo = {
          curveType: 'ELLIPSE',
          radius: numbers[0] ? numbers[0] * unitScale : 0, // Semi-major axis
          center: placement?.location,
          axis: placement?.axis
        };
        break;
      }

      case 'B_SPLINE_CURVE_WITH_KNOTS':
      case 'B_SPLINE_CURVE': {
        curveInfo = {
          curveType: 'B_SPLINE_CURVE'
        };
        break;
      }

      case 'TRIMMED_CURVE': {
        // TRIMMED_CURVE('', #basis_curve, ...)
        // Basis curve'ün tipini al
        const refs = extractRefs(params);
        if (refs[0]) {
          const basisEntity = entityMap.get(refs[0]);
          if (basisEntity) {
            curveInfo = {
              curveType: basisEntity.type === 'CIRCLE' ? 'CIRCLE' :
                         basisEntity.type === 'LINE' ? 'LINE' :
                         basisEntity.type === 'ELLIPSE' ? 'ELLIPSE' : 'TRIMMED_CURVE'
            };

            // Basis curve'den radius al
            if (basisEntity.type === 'CIRCLE') {
              const basisRefs = extractRefs(basisEntity.params);
              const basisNumbers = extractNumbers(basisEntity.params);
              const placement = basisRefs[0] ? placements.get(basisRefs[0]) : null;

              curveInfo.radius = basisNumbers[0] ? basisNumbers[0] * unitScale : 0;
              curveInfo.center = placement?.location;
              curveInfo.axis = placement?.axis;
            }
          }
        }
        break;
      }

      case 'POLYLINE': {
        curveInfo = {
          curveType: 'LINE' // Polyline'ı düz çizgi olarak kabul et
        };
        break;
      }
    }

    if (curveInfo) {
      curves[id] = curveInfo;
    }
  }

  return curves;
}

// ============================================================================
// ADVANCED_FACE PARSER
// ============================================================================

function parseAdvancedFaces(
  entityMap: Map<string, { type: string; params: string }>,
  surfaces: Record<string, SurfaceInfo>
): AdvancedFaceInfo[] {
  const advancedFaces: AdvancedFaceInfo[] = [];
  let faceIndex = 0;

  for (const [id, { type, params }] of entityMap) {
    if (type === 'ADVANCED_FACE') {
      // ADVANCED_FACE('', (#bounds...), #surface, .T./.F.)
      const refs = extractRefs(params);

      // Surface referansını bul (son referans genellikle surface)
      let surfaceId = '';
      let surfaceType = 'UNKNOWN';
      let surfaceData: SurfaceInfo = { surfaceType: 'UNKNOWN' };

      // Referansları ters sırayla kontrol et
      for (let i = refs.length - 1; i >= 0; i--) {
        if (surfaces[refs[i]]) {
          surfaceId = refs[i];
          surfaceType = surfaces[refs[i]].surfaceType;
          surfaceData = surfaces[refs[i]];
          break;
        }
      }

      advancedFaces.push({
        faceIndex,
        surfaceId,
        surfaceType,
        surfaceData
      });

      faceIndex++;
    }
  }

  // Debug: Parse edilen face tipleri
  const faceTypeStats: Record<string, { count: number; withRadius: number }> = {};
  for (const face of advancedFaces) {
    const type = face.surfaceType;
    if (!faceTypeStats[type]) {
      faceTypeStats[type] = { count: 0, withRadius: 0 };
    }
    faceTypeStats[type].count++;
    if (face.surfaceData.radius && face.surfaceData.radius > 0) {
      faceTypeStats[type].withRadius++;
    }
  }

  return advancedFaces;
}

// ============================================================================
// EDGE_CURVE PARSER
// ============================================================================

function parseEdgeCurves(
  entityMap: Map<string, { type: string; params: string }>,
  curves: Record<string, CurveInfo>
): EdgeCurveInfo[] {
  const edgeCurves: EdgeCurveInfo[] = [];
  let edgeIndex = 0;

  for (const [id, { type, params }] of entityMap) {
    if (type === 'EDGE_CURVE') {
      // EDGE_CURVE('', #start_vertex, #end_vertex, #curve, .T./.F.)
      const refs = extractRefs(params);

      let curveId = '';
      let curveType = 'UNKNOWN';
      let curveData: CurveInfo = { curveType: 'UNKNOWN' };

      // Curve referansını bul
      for (const ref of refs) {
        if (curves[ref]) {
          curveId = ref;
          curveType = curves[ref].curveType;
          curveData = curves[ref];
          break;
        }
      }

      edgeCurves.push({
        edgeIndex,
        curveId,
        curveType,
        curveData
      });

      edgeIndex++;
    }
  }

  return edgeCurves;
}

// ============================================================================
// RADIUS GROUPS BUILDER
// ============================================================================

function buildRadiusGroups(
  advancedFaces: AdvancedFaceInfo[],
  edgeCurves: EdgeCurveInfo[]
): {
  faceRadiusGroups: Record<string, number[]>;
  edgeRadiusGroups: Record<string, number[]>;
  radiusGroups: Record<string, string[]>;
} {
  const faceRadiusGroups: Record<string, number[]> = {};
  const edgeRadiusGroups: Record<string, number[]> = {};
  const radiusGroups: Record<string, string[]> = {};

  // Face radius grupları
  for (const face of advancedFaces) {
    const radius = face.surfaceData.radius || face.surfaceData.minorRadius || 0;
    if (radius > 0.001) { // 0.001 mm'den küçük radius'ları atla
      const key = radius.toFixed(4);
      if (!faceRadiusGroups[key]) {
        faceRadiusGroups[key] = [];
      }
      faceRadiusGroups[key].push(face.faceIndex);
    }
  }

  // Edge radius grupları
  for (const edge of edgeCurves) {
    const radius = edge.curveData.radius || 0;
    if (radius > 0.001) {
      const key = radius.toFixed(4);
      if (!edgeRadiusGroups[key]) {
        edgeRadiusGroups[key] = [];
      }
      edgeRadiusGroups[key].push(edge.edgeIndex);

      // Eski format uyumluluğu için radiusGroups'a da ekle
      if (!radiusGroups[key]) {
        radiusGroups[key] = [];
      }
      radiusGroups[key].push(edge.curveId);
    }
  }


  return { faceRadiusGroups, edgeRadiusGroups, radiusGroups };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function extractRefs(params: string): string[] {
  const refs: string[] = [];
  const matches = params.match(/#(\d+)/g) || [];
  for (const m of matches) {
    refs.push(m.substring(1));
  }
  return refs;
}

function extractNumbers(params: string): number[] {
  const numbers: number[] = [];
  // Parantez içindeki koordinatları atla, sadece serbest sayıları al
  // Önce parantez içlerini temizle
  const cleaned = params.replace(/\([^)]*\)/g, '');
  const matches = cleaned.match(/(?<![#\d])(-?[\d.]+(?:[eE][+-]?\d+)?)/g) || [];
  for (const m of matches) {
    const num = parseFloat(m);
    if (!isNaN(num)) {
      numbers.push(num);
    }
  }
  return numbers;
}

// ============================================================================
// EXPORTS
// ============================================================================

export { parseStepTopologyJs };

// Color chain resolution (eski uyumluluk için)
const colorCache = new Map<string, [number, number, number]>();

export function resolveColorChainCached(
  entityId: string,
  entityMap: Map<string, any>,
  styleCache?: Map<string, any>
): [number, number, number] {
  const cached = colorCache.get(entityId);
  if (cached) return cached;

  const color: [number, number, number] = [0.7, 0.7, 0.7];
  colorCache.set(entityId, color);
  return color;
}

export function clearColorCache(): void {
  colorCache.clear();
}
